import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Modal } from '@/components/ui/Modal'
import { getLeadActivity, type LeadActivityItem } from '@/api/lead-activity'
import { LeadActivityList, LeadActivityRow } from '@/components/inspections/LeadActivity'

/**
 * Раздел «Активность» (заменяет «Историю изменений»). Компактная плашка:
 * последние 3 события + кнопка «Вся активность (N)» → модалка с полной лентой
 * (как транскрибация звонка) с фильтром по типу события. Данные из RPC
 * get_lead_activity. «Коммуникации» остаются отдельной секцией.
 */
const PREVIEW_COUNT = 3

// Группы для фильтра в полной ленте. Чип показывается, только если в ленте есть
// хотя бы одно событие этого типа. created/order попадают только в «Все».
const FILTERS: { key: string; kinds: LeadActivityItem['kind'][]; label: string }[] = [
  { key: 'call', kinds: ['call', 'call_result'], label: 'Звонки' },
  { key: 'sms', kinds: ['sms'], label: 'SMS' },
  { key: 'status', kinds: ['status'], label: 'Статусы' },
  { key: 'field_change', kinds: ['field_change'], label: 'Правки полей' },
  { key: 'reminder', kinds: ['reminder'], label: 'Напоминания' },
  { key: 'event', kinds: ['event'], label: 'События' },
  { key: 'auction', kinds: ['auction'], label: 'Торги' },
  { key: 'comment', kinds: ['comment'], label: 'Комментарии' },
]

export function LeadActivitySection({ leadId }: { leadId: string }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('all')

  const { data, isLoading } = useQuery({
    queryKey: ['lead-activity', leadId],
    queryFn: () => getLeadActivity(leadId),
  })
  const items = data ?? []
  const preview = items.slice(0, PREVIEW_COUNT)

  // Какие чипы показывать — только присутствующие типы.
  const availableFilters = useMemo(
    () => FILTERS.filter((f) => items.some((it) => f.kinds.includes(it.kind))),
    [items],
  )
  const activeFilter = FILTERS.find((f) => f.key === filter)
  const filtered = activeFilter ? items.filter((it) => activeFilter.kinds.includes(it.kind)) : items

  function openModal() {
    setFilter('all')
    setOpen(true)
  }

  return (
    <>
      <h2 className="stryzh-heading-card mb-4">{t('leads.detail.secActivity')}</h2>

      {isLoading ? (
        <div className="stryzh-text-muted">{t('leads.detail.loading')}</div>
      ) : items.length === 0 ? (
        <p className="stryzh-text-muted">{t('leads.detail.activityEmpty')}</p>
      ) : (
        <>
          <ol className="space-y-2">
            {preview.map((it, i) => (
              <LeadActivityRow key={`${it.kind}-${it.at}-${i}`} item={it} />
            ))}
          </ol>
          {items.length > preview.length ? (
            <button
              type="button"
              onClick={openModal}
              className="mt-3 text-[length:var(--stryzh-font-size-sm)] font-medium text-[color:var(--stryzh-color-accent-orange)] hover:underline"
            >
              {t('leads.detail.activityAll', { n: items.length })} →
            </button>
          ) : null}
        </>
      )}

      {open ? (
        <Modal title={t('leads.detail.secActivity')} onClose={() => setOpen(false)}>
          <div className="mb-3 flex flex-wrap gap-1.5">
            <FilterChip label={`Все (${items.length})`} active={filter === 'all'} onClick={() => setFilter('all')} />
            {availableFilters.map((f) => {
              const n = items.filter((it) => f.kinds.includes(it.kind)).length
              return (
                <FilterChip key={f.key} label={`${f.label} (${n})`} active={filter === f.key} onClick={() => setFilter(f.key)} />
              )
            })}
          </div>
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            {filtered.length === 0 ? (
              <p className="stryzh-text-muted">{t('leads.detail.activityEmpty')}</p>
            ) : (
              <LeadActivityList items={filtered} />
            )}
          </div>
        </Modal>
      ) : null}
    </>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-full bg-[color:var(--stryzh-color-accent-orange)] px-2.5 py-1 text-[length:var(--stryzh-font-size-xs)] font-medium text-black'
          : 'rounded-full border border-[color:var(--stryzh-color-border-subtle)] px-2.5 py-1 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)] hover:bg-[rgba(255,255,255,0.05)]'
      }
    >
      {label}
    </button>
  )
}
